import {
  augmentPathologiesOnImage,
  getPatientIdFromS3Path,
} from './annotation-utils/annotation-utils';
import AnnotatorConstants from './annotator.constants';

const DEFAULT_STATE = {
  imageAnalyses: {},
  patients: {},
  confidenceThresholds: {
    ...AnnotatorConstants.DEFAULT_CONFIDENCE_THRESHOLDS,
  },
  isPPDEnabled: false,
  searchingPatients: false,
};

function handleViewPatient(state: any, action: any) {
  const { patientId } = action.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        displayed: true,
      },
    },
  };
}

function handleResetImages(state: any) {
  return {
    ...state,
    patients: {},
    imageAnalyses: {},
  };
}

/**
 * Give each new user annotation ID, and also an imageId to identify which image it came from
 */
function handleAddUserAnnotationSuccess(state: any, action: any) {
  const { image, selectedPatientId } = action.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [image.objectId]: image,
        },
      },
    },
  };
}

function handleDeleteUserAnnotationSuccess(state: any, action: any) {
  const { image, selectedPatientId } = action.data;
  // const { result } = action.response.data;

  return {
    ...state,
    // imageAnalyses: {
    //   ...state.imageAnalyses,
    //   [result.objectId]: result,
    // },
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [image.objectId]: image,
        },
      },
    },
  };
}

function handleSavePatientDataSuccess(state: any, action: any) {
  const { patientId, age, gender } = action.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        age,
        gender,
      },
    },
  };
}

function handleParsePatientXMLSuccess(state: any, action: any) {
  const {
    data: { patientId, patientInfo },
  } = action;

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        ...patientInfo,
      },
    },
  };
}

/**
 *
 * @param {*} state
 * @param {*} action
 */
function handleFetchAiResultsSuccess(state: any, action: any) {
  const {
    response: { data = {} },
  } = action;

  const {
    objectId: imageObjectId,
    patientInfo = {},
    imageId,
    dataSource,
  } = data;
  const imageAiAnalysis = { ...data };
  let { patientId } = patientInfo;

  const objectId = imageObjectId ?? imageId;

  if (!patientInfo.analysisDate && dataSource !== 'NUCLEUS') {
    patientInfo.analysisDate = new Date().toISOString();
  }

  if (!patientId) {
    // We are dealing with images that do not have metadata
    const { s3Path } = imageAiAnalysis;
    patientId = getPatientIdFromS3Path(s3Path);
    patientInfo.patientId = patientId;
  }
  const currentImageInState =
    state.patients[patientId]?.images?.[objectId] || {};
  const { userAnnotations: userAnnotationsFromState } =
    currentImageInState ?? [];
  const augmentedImageAiAnalysis = augmentPathologiesOnImage(
    imageAiAnalysis,
    objectId,
    userAnnotationsFromState,
  );

  const patientImages = {
    ...(state.patients[patientId] || {}).images,
    [objectId]: { ...augmentedImageAiAnalysis, rotation: 0 },
  };

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        ...patientInfo,
        displayed: state.patients[patientId]
          ? state.patients[patientId].displayed
          : false,
        dataSource,
        images: patientImages,
      },
    },
  };
}

export function updateImageRotationReducer(state: any, action: any) {
  const { selectedPatientId, image } = action.response.data;
  const { rotation } = image;
  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [image.objectId]: { ...image, rotation },
        },
      },
    },
  };
}

function handleUpdateValidationSuccess(state: any, action: any) {
  const { updatedImage: image, selectedPatientId } = action.response.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [image.objectId]: image,
        },
      },
    },
  };
}

function handleFetchConfidenceThresholdsSuccess(state: any, action: any) {
  return {
    ...state,
    confidenceThresholds: {
      ...action.response.data,
    },
  };
}

function handlePatientImageFetchSuccess(state: any, action: any) {
  const { result } = action.response.data;
  const { patientId } = action.data;
  const currentPatientImages = { ...state.patients[patientId].images };
  const currentAnalysisPaths = Object.values(currentPatientImages).map(
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    (a) => a.sourceFileName,
  );

  // Look for the patient image analyses that have file names not already analyzed
  const newImages = result.filter(
    (analysis: any) => !currentAnalysisPaths.includes(analysis.sourceFileName),
  );

  if (newImages.length) {
    newImages.forEach((img: any) => {
      currentPatientImages[img.objectId] = img;
    });

    return {
      ...state,
      patients: {
        ...state.patients,
        [patientId]: {
          ...state.patients[patientId],
          images: currentPatientImages,
        },
      },
    };
  }

  return {
    ...state,
  };
}

export function handleNewAiStatsFetchSuccess(state: any, action: any) {
  const { aiStatistics, productionValue } = action.response.data;

  return {
    ...state,
    patients: {
      ...state.patients,
    },
    aiStatistics,
    productionValue,
  };
}

function handleUpdateToothNumber(state: any, action: any) {
  const { updatedImage, selectedPatientId } = action.response.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [updatedImage.objectId]: updatedImage,
        },
      },
    },
  };
}

function handleAddUserAnnotationFailure(state: any, action: any) {
  const { image, selectedPatientId } = action.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: {
          ...state.patients[selectedPatientId].images,
          [image.objectId]: image,
        },
      },
    },
  };
}

function resetImagesForPatient(state: any, action: any) {
  const {
    response: { data = {} },
  } = action;

  const { patientId } = data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        images: {},
      },
    },
  };
}

function handleSaveUserModificationsSuccess(state: any, action: any) {
  const { patientId, imageId, modificationsFromImageToolbar } =
    action.response.data;

  return {
    ...state,
    patients: {
      ...state.patients,
      [patientId]: {
        ...state.patients[patientId],
        images: {
          ...state.patients[patientId].images,
          [imageId]: {
            ...state.patients[patientId].images[imageId],
            userModifications: modificationsFromImageToolbar,
          },
        },
      },
    },
  };
}

export function swapImageIndexesReducer(state: any, action: any) {
  const { selectedPatientId, image1, image2 } = action.response.data;

  const imagesWithUpdatedIndices = {
    ...state.patients[selectedPatientId].images,
  };
  if (image2.imageId) {
    imagesWithUpdatedIndices[image2.imageId] = {
      ...state.patients[selectedPatientId].images[image2.imageId],
      fmxIndex: image1.fmxIndex,
      imageGroup: image1.imageGroup,
    };
  }
  if (image1.imageId) {
    imagesWithUpdatedIndices[image1.imageId] = {
      ...state.patients[selectedPatientId].images[image1.imageId],
      fmxIndex: image2.fmxIndex,
      imageGroup: image2.imageGroup,
    };
  }

  return {
    ...state,
    patients: {
      ...state.patients,
      [selectedPatientId]: {
        ...state.patients[selectedPatientId],
        images: imagesWithUpdatedIndices,
      },
    },
  };
}

function updateUserName(state: any, action: any) {
  const { id, name } = action.data;
  return {
    ...state,
    patients: {
      ...state.patients,
      [id]: {
        ...state.patients[id],
        name,
      },
    },
  };
}

function clearPatientImages(state: any, action: any) {
  const { id } = action.data;
  return {
    ...state,
    patients: {
      ...state.patients,
      [id]: {
        ...state.patients[id],
        images: {},
      },
    },
  };
}

const actionsMap = {
  [AnnotatorConstants.ADD_USER_ANNOTATION_SUCCESS]:
    handleAddUserAnnotationSuccess,
  [AnnotatorConstants.DELETE_USER_ANNOTATION_SUCCESS]:
    handleDeleteUserAnnotationSuccess,
  [AnnotatorConstants.DELETE_USER_ANNOTATION_FAILURE]:
    handleAddUserAnnotationFailure,
  [AnnotatorConstants.SAVE_PATIENT_DATA_SUCCESS]: handleSavePatientDataSuccess,
  [AnnotatorConstants.FETCH_AI_RESULTS_SUCCESS]: handleFetchAiResultsSuccess,
  [AnnotatorConstants.FETCH_CONFIDENCE_THRESHOLDS_SUCCESS]:
    handleFetchConfidenceThresholdsSuccess,
  [AnnotatorConstants.UPDATE_AI_RESULT_SUCCESS]: handleUpdateValidationSuccess,
  [AnnotatorConstants.UPDATE_USER_FINDING_SUCCESS]:
    handleUpdateValidationSuccess,
  [AnnotatorConstants.RESET_IMAGES]: handleResetImages,
  [AnnotatorConstants.VIEW_PATIENT]: handleViewPatient,
  [AnnotatorConstants.FETCH_PATIENT_ANALYSES_SUCCESS]:
    handlePatientImageFetchSuccess,
  [AnnotatorConstants.PARSE_PATIENT_XML_SUCCESS]: handleParsePatientXMLSuccess,
  [AnnotatorConstants.UPDATE_TOOTH_NUMBER]: handleUpdateToothNumber,
  [AnnotatorConstants.HANDLE_IMAGE_ROTATION]: updateImageRotationReducer,
  [AnnotatorConstants.RESET_IMAGES_FOR_PATIENT]: resetImagesForPatient,
  [AnnotatorConstants.NEW_AI_STATS_FETCH_SUCCESS]: handleNewAiStatsFetchSuccess,
  [AnnotatorConstants.SAVE_USER_MODIFICATIONS_SUCCESS]:
    handleSaveUserModificationsSuccess,
  [AnnotatorConstants.SWAP_IMAGE_INDICES]: swapImageIndexesReducer,
  [AnnotatorConstants.UPDATE_USER_NAME]: updateUserName,
  [AnnotatorConstants.CLEAR_PATIENT_IMAGES]: clearPatientImages,
};

export default function Annotator(state = DEFAULT_STATE, action: any) {
  const { type } = action;

  if (actionsMap[type]) {
    return actionsMap[type](state, action);
  }
  return state;
}
